import FocusInput from '../FocusInput';
import {Box, Select, Switch} from '@sproutsocial/racine';
import {SyntheticEvent, useCallback, useEffect, useId, useRef} from 'react';
import {InvoiceLineItem, InvoiceLineItemType} from 'shared/src/models';

interface InvoiceLineItemEditFormProps {
	item: InvoiceLineItem;
	onChange: (updatedLineItem: InvoiceLineItem) => void;
}

const InvoiceLineItemEditForm = ({item, onChange}: InvoiceLineItemEditFormProps) => {
	const nameId = useId();
	const descriptionId = useId();
	const quantityId = useId();
	const costId = useId();
	const typeId = useId();

	const currentItemRef = useRef<InvoiceLineItem>();

	useEffect(() => {
		currentItemRef.current = item;
	}, [currentItemRef, item]);

	const onNameUpdated = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			onChange({
				...currentItemRef.current!,
				name: event.currentTarget.value,
			});
		},
		[onChange],
	);

	const onDescriptionUpdated = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			onChange({
				...currentItemRef.current!,
				description: event.currentTarget.value,
			});
		},
		[onChange],
	);

	const onQuantityUpdated = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			const {value} = event.currentTarget;

			onChange({
				...currentItemRef.current!,
				quantity: value ? parseFloat(value) : null,
			});
		},
		[onChange],
	);

	const onPriceUpdated = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			const {value} = event.currentTarget;

			onChange({
				...currentItemRef.current!,
				price: value ? Math.floor(parseFloat(value) * 100) : 0,
			});
		},
		[onChange],
	);

	const onTypeChanged = useCallback(
		(event: SyntheticEvent<HTMLSelectElement>) => {
			onChange({
				...currentItemRef.current!,
				type: event.currentTarget.value as InvoiceLineItemType,
				taxed:
					currentItemRef.current!.type !== InvoiceLineItemType.Cost
						? false
						: currentItemRef.current!.taxed,
			});
		},
		[onChange],
	);

	const onTaxedToggled = useCallback(
		(event: SyntheticEvent<HTMLButtonElement>, checked: boolean) => {
			onChange({
				...currentItemRef.current!,
				taxed: checked,
			});
		},
		[onChange],
	);

	return (
		<Box display='flex' alignItems='center' mb='space.300'>
			<Box flex={3} mr='space.300'>
				<FocusInput id={nameId} name={nameId} value={item.name} onChange={onNameUpdated} />
			</Box>
			<Box flex={3} mr='space.300'>
				<FocusInput
					id={descriptionId}
					name={descriptionId}
					value={item.description ?? ''}
					onChange={onDescriptionUpdated}
				/>
			</Box>
			<Box flex={1} mr='space.300'>
				<FocusInput
					id={quantityId}
					name={quantityId}
					type='number'
					value={typeof item.quantity === 'number' ? item.quantity.toString() : ''}
					onChange={onQuantityUpdated}
					inputProps={{
						min: 0,
					}}
				/>
			</Box>
			<Box flex={1} mr='space.300'>
				<FocusInput
					id={costId}
					name={costId}
					type='number'
					value={(item.price / 100).toFixed(2)}
					onChange={onPriceUpdated}
					inputProps={{
						min: 0,
					}}
				/>
			</Box>
			<Box flex={1} mr='space.300'>
				<Select id={typeId} name={typeId} value={item.type} onChange={onTypeChanged}>
					<option value={InvoiceLineItemType.Cost}>Cost</option>
					<option value={InvoiceLineItemType.Credit}>Credit</option>
					<option value={InvoiceLineItemType.Payment}>Payment</option>
				</Select>
			</Box>
			<Box flex={1}>
				<Switch
					checked={Boolean(item.taxed)}
					disabled={item.type !== InvoiceLineItemType.Cost}
					onClick={onTaxedToggled}
				/>
			</Box>
		</Box>
	);
};

export default InvoiceLineItemEditForm;
